import { FunctionComponent, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { BatchApiStatusHandler, HomeIcon } from '../components';
import { AccountsTable } from '../components/AccountsTable';
import { AgencyDetails, LegalEntityRegion } from '../types';
import { useBatchApi } from '../hooks';
import { useGlobalContext } from '../contexts/globalContext';
import {
  getLoginDataFromAgencies,
  getRegistrationInfoFromAgencies,
} from '../utils/agency';

type LocationDetailAccountsProps = {
  legalEntityRegion: LegalEntityRegion;
  agencies: AgencyDetails[];
};

export const LocationDetailAccounts: FunctionComponent<
  LocationDetailAccountsProps
> = ({ legalEntityRegion, agencies }) => {
  const stateCode = legalEntityRegion.region.code.toLowerCase();
  const { globalState } = useGlobalContext();
  const isIncorporationState =
    globalState.user.legal_entity.incorporation_region?.code.toLowerCase() ===
    stateCode;

  const logins = useMemo(() => getLoginDataFromAgencies(agencies), [agencies]);
  const registrationInfo = useMemo(
    () => getRegistrationInfoFromAgencies(agencies),
    [agencies]
  );

  return (
    <div className="min-h-full space-y-6 p-6">
      <section>
        <h2 className="font-bold text-zinc-800">Registrations</h2>
        <div className="mt-6 border bg-white">
          <div className="flex flex-1 divide-x">
            <div className="flex flex-1 flex-col justify-start p-6">
              <div className="text-xs font-medium uppercase leading-6 tracking-widest text-zinc-600">
                Registration Information
              </div>
              {isIncorporationState && (
                <div className="mt-1 flex items-center space-x-2 text-sm">
                  <span className="rounded-full bg-yellow-400 p-2 text-white">
                    <HomeIcon className="size-4" />
                  </span>
                  <div>
                    <div className="text-sm font-bold leading-none text-gray-700">
                      Incorporation State
                    </div>
                    <Link
                      to={`/locations/usa/${stateCode}/certificate-of-good-standing`}
                      className="text-teal-600 hover:text-teal-700"
                    >
                      Request a Certificate of Good Standing...
                    </Link>
                  </div>
                </div>
              )}
              <ul className="mt-2 divide-y text-sm text-gray-500">
                {registrationInfo?.length > 0 ? (
                  registrationInfo.map((info) => (
                    <li
                      className="flex justify-between py-2 text-zinc-800"
                      key={info.name}
                    >
                      <span>{info.name}</span>
                      <span className="font-semibold">{info.value}</span>
                    </li>
                  ))
                ) : (
                  <div>
                    <span className="italic text-gray-500">None found</span>
                  </div>
                )}
              </ul>
            </div>
            <div className="flex flex-1 flex-col justify-start overflow-hidden border-r p-6 2xl:flex-1">
              <div className="text-xs font-medium uppercase leading-6 tracking-widest text-zinc-600">
                Agencies
              </div>
              <ul className="mt-2 list-inside list-disc space-y-1 whitespace-nowrap text-sm">
                {agencies?.length > 0 ? (
                  agencies.map((agency) => (
                    <li
                      key={agency.id}
                      className="truncate font-medium leading-7 text-zinc-800"
                    >
                      {agency.name}
                    </li>
                  ))
                ) : (
                  <div>
                    <span className="italic text-gray-500">None found</span>
                  </div>
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>
      <section>
        <h2 className="font-bold text-zinc-800">Login Credentials</h2>
        <div className="mt-6 bg-white">
          <AccountsTable logins={logins} />
        </div>
      </section>
    </div>
  );
};

type LocationDetailAccountsViewProps = {
  legalEntityRegion: LegalEntityRegion;
};

export const LocationDetailAccountsView: FunctionComponent<
  LocationDetailAccountsViewProps
> = ({ legalEntityRegion }) => {
  const locationId = legalEntityRegion.region.code.toLowerCase();

  const batchResponse = useBatchApi([
    {
      url: `/api/legal_entity/locations/${locationId}`,
      method: 'GET',
    },
    {
      url: `/api/legal_entity/locations/${locationId}/agencies`,
      method: 'GET',
    },
  ]);

  const componentPropsFn = ([
    legalEntityRegionResponse,
    agenciesResponse,
  ]: any[]): LocationDetailAccountsProps => {
    return {
      legalEntityRegion: legalEntityRegionResponse,
      agencies: agenciesResponse,
    };
  };

  return (
    <BatchApiStatusHandler
      batchResponse={batchResponse}
      component={LocationDetailAccounts}
      componentProps={componentPropsFn}
    />
  );
};
